// ========================================
// Data Manager - Facade Pattern
// Delegates to specialized services
// ========================================

const DataManager = {
    // Storage keys
    KEYS: {
        PROFESSORS: 'ecpv_professors',
        FORMATIONS: 'ecpv_formations',
        SUBJECTS: 'ecpv_subjects',
        CLASSES: 'ecpv_classes',
        GLOBAL_SETTINGS: 'ecpv_global_settings',
        MESSAGES: 'ecpv_messages',
        NOTIFICATIONS: 'ecpv_notifications',
        JUSTIFICANTES: 'ecpv_justificantes',
        STUDENTS: 'ecpv_students',
        PROJECTS: 'ecpv_warehouse_projects',
        LEADS: 'ecpv_leads',
        STUDY_BLOCKS: 'ecpv_study_blocks'
    },

    // ========================================
    // Generic CRUD Operations
    // ========================================

    getAll(key) {
        const data = FileStorage.getItem(key);
        return data ? JSON.parse(data) : [];
    },

    saveAll(key, data) {
        FileStorage.setItem(key, JSON.stringify(data));
    },

    getById(key, id) {
        const items = this.getAll(key);
        return items.find(item => item.id == id || String(item.id) === String(id));
    },

    add(key, item) {
        const items = this.getAll(key);
        items.push(item);
        this.saveAll(key, items);
        return item;
    },

    update(key, id, updates) {
        const items = this.getAll(key);
        const index = items.findIndex(item => item.id == id);
        if (index !== -1) {
            items[index] = { ...items[index], ...updates };
            this.saveAll(key, items);
            return items[index];
        }
        return null;
    },

    delete(key, id) {
        const items = this.getAll(key);
        const filtered = items.filter(item => item.id != id);
        this.saveAll(key, filtered);
        return filtered.length < items.length;
    },

    // ========================================
    // Professor Operations (Delegate to ProfessorService)
    // ========================================

    getProfessors() {
        return ProfessorService.getProfessors();
    },

    getProfessor(id) {
        return ProfessorService.getProfessor(id);
    },

    createProfessor(data) {
        return ProfessorService.createProfessor(data);
    },

    updateProfessor(id, data) {
        return ProfessorService.updateProfessor(id, data);
    },

    deleteProfessor(id) {
        return ProfessorService.deleteProfessor(id);
    },

    getProfessorWeeklyHours(professorId) {
        return ProfessorService.getProfessorWeeklyHours(professorId);
    },

    getProfessorMonthlyHours(professorId, year, month) {
        return ProfessorService.getProfessorMonthlyHours(professorId, year, month);
    },

    getProfessorAverageWeeklyHours(professorId) {
        return ProfessorService.getProfessorAverageWeeklyHours(professorId);
    },

    getProfessorTotalHours(professorId) {
        return ProfessorService.getProfessorTotalHours(professorId);
    },

    getProfessorWeeklyCost(professorId) {
        return ProfessorService.getProfessorWeeklyCost(professorId);
    },

    getProfessorMonthlyPayroll(professorId) {
        return ProfessorService.getProfessorMonthlyPayroll(professorId);
    },

    // ========================================
    // Formation Operations (Delegate to FormationService)
    // ========================================

    getFormations() {
        return FormationService.getFormations();
    },

    getFormation(id) {
        return FormationService.getFormation(id);
    },

    getFormationsByType(type) {
        return FormationService.getFormationsByType(type);
    },

    createFormation(data) {
        return FormationService.createFormation(data);
    },

    updateFormation(id, data) {
        return FormationService.updateFormation(id, data);
    },

    deleteFormation(id) {
        return FormationService.deleteFormation(id);
    },

    // ========================================
    // Subject Operations (Delegate to SubjectService)
    // ========================================

    getSubjects() {
        return SubjectService.getSubjects();
    },

    getSubject(id) {
        return SubjectService.getSubject(id);
    },

    getSubjectsByFormation(formationId) {
        return SubjectService.getSubjectsByFormation(formationId);
    },

    getSubjectsByProfessor(professorId) {
        return SubjectService.getSubjectsByProfessor(professorId);
    },

    createSubject(data) {
        return SubjectService.createSubject(data);
    },

    updateSubject(id, data) {
        return SubjectService.updateSubject(id, data);
    },

    deleteSubject(id) {
        return SubjectService.deleteSubject(id);
    },

    // ========================================
    // Class Session Operations (Delegate to ClassService)
    // ========================================

    getClasses() {
        return ClassService.getClasses();
    },

    getClass(id) {
        return ClassService.getClass(id);
    },

    getClassesByProfessor(professorId) {
        return ClassService.getClassesByProfessor(professorId);
    },

    getClassesByFormation(formationId) {
        return ClassService.getClassesByFormation(formationId);
    },

    getClassesBySubject(subjectId) {
        return ClassService.getClassesBySubject(subjectId);
    },

    createClass(data) {
        return ClassService.createClass(data);
    },

    updateClass(id, data) {
        return ClassService.updateClass(id, data);
    },

    deleteClass(id) {
        return ClassService.deleteClass(id);
    },

    // ========================================
    // Message Operations (Delegate to MessageService)
    // ========================================

    getMessages() {
        if (typeof MessageService === 'undefined') return [];
        return MessageService.getAllMessages();
    },

    getMessage(id) {
        if (typeof MessageService === 'undefined') return null;
        return MessageService.getMessage(id);
    },

    getMessagesForUser(userId) {
        if (typeof MessageService === 'undefined') return [];
        return MessageService.getMessagesForUser(userId);
    },

    getUnreadCount(userId) {
        if (typeof MessageService === 'undefined') return 0;
        return MessageService.getUnreadCount(userId);
    },

    sendMessage(data) {
        if (typeof MessageService === 'undefined') return null;
        return MessageService.sendMessage(data);
    },

    markAsRead(messageId) {
        if (typeof MessageService === 'undefined') return false;
        return MessageService.markAsRead(messageId);
    },

    deleteMessage(id, username) {
        if (typeof MessageService === 'undefined') return false;
        return MessageService.deleteMessage(id, username);
    },

    moveToTrash(id, username) {
        if (typeof MessageService === 'undefined') return false;
        return MessageService.moveToTrash(id, username);
    },

    restoreMessage(id, username) {
        if (typeof MessageService === 'undefined') return false;
        return MessageService.restoreMessage(id, username);
    },

    permanentDeleteMessage(id, username) {
        if (typeof MessageService === 'undefined') return false;
        return MessageService.permanentDelete(id, username);
    },

    emptyTrash(username) {
        if (typeof MessageService === 'undefined') return false;
        return MessageService.emptyTrash(username);
    },

    // ========================================
    // Notification Operations
    // ========================================

    getNotifications(userId) {
        const all = this.getAll(this.KEYS.NOTIFICATIONS);
        // Filter by receiverId or 'all', excluding messages (which are shown in the mail icon)
        const user = AuthManager.getCurrentUser();
        return all.filter(n => (n.receiverUserId === user?.id || [userId, user?.id, user?.role, 'all'].includes(n.receiverId)) && !n.hidden && n.type !== 'message' && n.link !== 'mensajes').sort((a, b) => new Date(b.date) - new Date(a.date));
    },

    async addNotification(notification) {
        if (!notification || !notification.receiverId) return null;

        const all = this.getAll(this.KEYS.NOTIFICATIONS);
        // Prevenir duplicados idénticos para el mismo destinatario
        const existing = all.find(n =>
            n.receiverId === notification.receiverId &&
            n.title === notification.title &&
            n.message === notification.message &&
            (n.targetView || null) === (notification.targetView || null)
        );
        if (existing) {
            console.log(`[DataManager] Notificación duplicada evitada: "${notification.title}" para ${notification.receiverId}`);
            return existing;
        }

        try {
            const response = await fetch('/api/notifications', {
                method: 'POST', headers: { 'Content-Type': 'application/json', 'X-SICAF-CSRF': AuthManager.getCsrfToken() },
                body: JSON.stringify(notification)
            });
            const result = await response.json();
            if (!response.ok || !result.success) throw new Error('No se pudo guardar el aviso');
            const user = AuthManager.getCurrentUser();
            // Sent notices to other people are not part of our readable snapshot.
            if ([user.username, user.id, user.role, 'all'].includes(result.notification.receiverId)) {
                const updated = [...this.getAll(this.KEYS.NOTIFICATIONS), result.notification];
                FileStorage.dataStore.notifications = updated;
                BrowserState.setItem(this.KEYS.NOTIFICATIONS, JSON.stringify(updated));
            }
            return result.notification;
        } catch {
            if (typeof Utils !== 'undefined') Utils.showToast('No se pudo guardar el aviso. Revisa la conexión y tus permisos.', 'error');
            return null;
        }
    },

    markNotificationAsRead(id) {
        return this.update(this.KEYS.NOTIFICATIONS, id, { read: true });
    },

    deleteNotification(id) {
        return this.update(this.KEYS.NOTIFICATIONS, id, { hidden: true });
    },

    getUnreadNotificationsCount(userId) {
        const notifs = this.getNotifications(userId);
        return notifs.filter(n => !n.read).length;
    },

    // ========================================
    // Conflict Detection (Delegate to ConflictService)
    // ========================================

    checkProfessorConflict(professorId, dayOfWeek, startTime, endTime, date = null, excludeClassId = null) {
        return ConflictService.checkProfessorConflict(professorId, dayOfWeek, startTime, endTime, excludeClassId, date);
    },

    timesOverlap(start1, end1, start2, end2) {
        return ConflictService.timesOverlap(start1, end1, start2, end2);
    },

    timeToMinutes(time) {
        return ConflictService.timeToMinutes(time);
    },

    // ========================================
    // Settings & Holidays
    // ========================================

    getHolidays() {
        const settings = this.getAll(this.KEYS.GLOBAL_SETTINGS);
        if (Array.isArray(settings) && settings.length > 0) {
            return settings[0].holidays || [];
        }
        return [];
    },

    isHoliday(dateStr) {
        const holidays = this.getHolidays();
        return holidays.includes(dateStr);
    },

    // ========================================
    // Statistics (Delegate to StatisticsService)
    // ========================================

    getStatistics() {
        return StatisticsService.getStatistics();
    },

    // ========================================
    // Data Export/Import
    // ========================================

    exportData() {
        return {
            version: '1.0',
            exportDate: new Date().toISOString(),
            data: {
                professors: this.getAll(this.KEYS.PROFESSORS),
                formations: this.getAll(this.KEYS.FORMATIONS),
                subjects: this.getAll(this.KEYS.SUBJECTS),
                classes: this.getAll(this.KEYS.CLASSES),
                globalSettings: this.getAll(this.KEYS.GLOBAL_SETTINGS),
                messages: this.getAll(this.KEYS.MESSAGES),
                notifications: this.getAll(this.KEYS.NOTIFICATIONS),
                justificantes: this.getAll(this.KEYS.JUSTIFICANTES)
            }
        };
    },

    // ========================================
    // Study Blocks Operations
    // ========================================

    // Study blocks are the school's own (Plan de Estudios → Configuración de Bloques), per
    // formation type. A type without any starts with one neutral block the school can rename.
    neutralStudyBlocks() {
        return [{ id: 'GENERAL', name: 'GENERAL', color: '#0f766e', order: 1 }];
    },

    getAllStudyBlocks() {
        const stored = FileStorage.getItem(this.KEYS.STUDY_BLOCKS);
        let blocksMap = null;
        if (stored) {
            try {
                blocksMap = typeof stored === 'string' ? JSON.parse(stored) : stored;
            } catch (e) {
                console.warn('Error parsing study blocks:', e);
            }
        }
        return blocksMap && typeof blocksMap === 'object' && !Array.isArray(blocksMap) ? blocksMap : {};
    },

    getStudyBlocks(category) {
        const list = this.getAllStudyBlocks()[String(category || '').toLowerCase()];
        return Array.isArray(list) && list.length ? list : this.neutralStudyBlocks();
    },

    saveAllStudyBlocks(blocksMap) {
        FileStorage.setItem(this.KEYS.STUDY_BLOCKS, JSON.stringify(blocksMap));
    },

    saveStudyBlocks(category, blocks) {
        const all = this.getAllStudyBlocks();
        all[String(category || '').toLowerCase()] = blocks;
        this.saveAllStudyBlocks(all);
        return blocks;
    },

    addStudyBlock(category, block) {
        const blocks = this.getStudyBlocks(category);
        const id = block.id || ('BLOCK_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 4)).toUpperCase();
        const newBlock = {
            id,
            name: (block.name || 'Nuevo Bloque').trim().toUpperCase(),
            color: block.color || '#0f766e',
            order: block.order || (blocks.length + 1)
        };
        blocks.push(newBlock);
        this.saveStudyBlocks(category, blocks);
        return newBlock;
    },

    updateStudyBlock(category, blockId, updates) {
        const blocks = this.getStudyBlocks(category);
        const index = blocks.findIndex(b => b.id === blockId);
        if (index !== -1) {
            blocks[index] = { ...blocks[index], ...updates };
            if (updates.name) {
                blocks[index].name = updates.name.trim().toUpperCase();
            }
            this.saveStudyBlocks(category, blocks);
            return blocks[index];
        }
        return null;
    },

    deleteStudyBlock(category, blockId) {
        const blocks = this.getStudyBlocks(category);
        const filtered = blocks.filter(b => b.id !== blockId);
        this.saveStudyBlocks(category, filtered);

        // Reassign all subjects belonging to formations of this category that had this studyBlock to 'OTROS'
        const formations = this.getFormations().filter(f => (f.type || '').toLowerCase() === (category || '').toLowerCase());
        const formationIds = new Set(formations.map(f => String(f.id)));
        const allSubjects = this.getAll(this.KEYS.SUBJECTS);
        let modified = false;

        allSubjects.forEach(sub => {
            if (formationIds.has(String(sub.formationId)) && sub.studyBlock === blockId) {
                sub.studyBlock = 'OTROS';
                modified = true;
            }
        });

        if (modified) {
            this.saveAll(this.KEYS.SUBJECTS, allSubjects);
        }
        return true;
    },

    assignSubjectToBlock(subjectId, blockId) {
        return this.update(this.KEYS.SUBJECTS, subjectId, { studyBlock: blockId });
    }
};

// Make DataManager globally available
window.DataManager = DataManager;
